<h2 mat-dialog-title>{{ 'EDIT_MATCH.TITLE' | translate }}</h2>

<mat-dialog-content>
    <!-- Match times -->
    <section class="mb-4">
        <h3 class="section-title">{{ 'EDIT_MATCH.TIMES_TITLE' | translate }}</h3>
        <div class="row">
            <mat-form-field appearance="outline" class="col-12 col-md-4">
                <mat-label>{{ 'EDIT_MATCH.CREATED_AT' | translate }}</mat-label>
                <input
                    matInput
                    type="datetime-local"
                    [attr.aria-label]="'EDIT_MATCH.CREATED_AT' | translate"
                    title="{{ 'EDIT_MATCH.CREATED_AT' | translate }}"
                    [value]="toDatetimeLocal(match.createdAt)"
                    (change)="match.createdAt = fromDatetimeLocal($event)"
                />
            </mat-form-field>
            <mat-form-field appearance="outline" class="col-12 col-md-4">
                <mat-label>{{ 'EDIT_MATCH.STARTED_AT' | translate }}</mat-label>
                <input
                    matInput
                    type="datetime-local"
                    [attr.aria-label]="'EDIT_MATCH.STARTED_AT' | translate"
                    title="{{ 'EDIT_MATCH.STARTED_AT' | translate }}"
                    [value]="toDatetimeLocal(match.startedAt)"
                    (change)="match.startedAt = fromDatetimeLocal($event)"
                />
            </mat-form-field>
            <mat-form-field appearance="outline" class="col-12 col-md-4">
                <mat-label>{{ 'EDIT_MATCH.ENDED_AT' | translate }}</mat-label>
                <input
                    matInput
                    type="datetime-local"
                    [attr.aria-label]="'EDIT_MATCH.ENDED_AT' | translate"
                    title="{{ 'EDIT_MATCH.ENDED_AT' | translate }}"
                    [value]="toDatetimeLocal(match.endedAt)"
                    (change)="match.endedAt = fromDatetimeLocal($event)"
                />
            </mat-form-field>
        </div>
    </section>

    <!-- Sets -->
    <section>
        <div class="sets-header">
            <h3 class="section-title mb-0">{{ 'EDIT_MATCH.SETS_TITLE' | translate }}</h3>
            <button mat-stroked-button color="primary" (click)="addSet()">
                <mat-icon>add</mat-icon>
                {{ 'EDIT_MATCH.ADD_SET' | translate }}
            </button>
        </div>
        <mat-accordion>
            @for (set of match.sets; track $index; let setIx = $index) {
                <mat-expansion-panel>
                    <mat-expansion-panel-header>
                        <mat-panel-title>
                            {{
                                'EDIT_MATCH.SET_TITLE'
                                    | translate
                                        : {
                                              set: setIx + 1,
                                              scoreA: set.teamA.score,
                                              scoreB: set.teamB.score,
                                          }
                            }}
                        </mat-panel-title>
                    </mat-expansion-panel-header>

                    <!-- Player positions -->
                    <div class="positions-row">
                        <div class="team-block">
                            <span class="team-label">{{ 'EDIT_MATCH.TEAM_A' | translate }}</span>
                            <span class="position-item">
                                <mat-icon class="role-icon">security</mat-icon>
                                <span class="player-name">{{ set.teamA.defense.displayName }}</span>
                            </span>
                            @if (!isOneAgainstOne()) {
                                <span class="position-item">
                                    <mat-icon class="role-icon">sports_soccer</mat-icon>
                                    <span class="player-name">{{ set.teamA.offense.displayName }}</span>
                                </span>
                                <button
                                    mat-icon-button
                                    [matTooltip]="'EDIT_MATCH.SWAP_POSITIONS' | translate"
                                    (click)="swapPositions(setIx, 'A')"
                                >
                                    <mat-icon>swap_horiz</mat-icon>
                                </button>
                            }
                        </div>
                        <div class="team-block">
                            <span class="team-label">{{ 'EDIT_MATCH.TEAM_B' | translate }}</span>
                            <span class="position-item">
                                <mat-icon class="role-icon">security</mat-icon>
                                <span class="player-name">{{ set.teamB.defense.displayName }}</span>
                            </span>
                            @if (!isOneAgainstOne()) {
                                <span class="position-item">
                                    <mat-icon class="role-icon">sports_soccer</mat-icon>
                                    <span class="player-name">{{ set.teamB.offense.displayName }}</span>
                                </span>
                                <button
                                    mat-icon-button
                                    [matTooltip]="'EDIT_MATCH.SWAP_POSITIONS' | translate"
                                    (click)="swapPositions(setIx, 'B')"
                                >
                                    <mat-icon>swap_horiz</mat-icon>
                                </button>
                            }
                        </div>
                    </div>

                    <mat-divider class="my-3"></mat-divider>

                    <!-- Goals list -->
                    <h4 class="subsection-title">{{ 'EDIT_MATCH.GOALS_TITLE' | translate }}</h4>
                    @if (!set.goals.length) {
                        <p class="text-muted no-goals">{{ 'EDIT_MATCH.NO_GOALS' | translate }}</p>
                    }
                    @for (goal of set.goals; track $index; let goalIx = $index) {
                        <div class="goal-row">
                            <mat-icon
                                class="goal-icon"
                                [matTooltip]="
                                    goal.role === SetScoreGoalRole.Offense
                                        ? ('EDIT_MATCH.ROLE_OFFENSE' | translate)
                                        : ('EDIT_MATCH.ROLE_DEFENSE' | translate)
                                "
                            >
                                {{ goal.role === SetScoreGoalRole.Offense ? 'sports_soccer' : 'security' }}
                            </mat-icon>
                            <span class="goal-player">{{ getPlayerName(setIx, goal.idPlayer) }}</span>
                            <mat-form-field appearance="outline" class="goal-time-field">
                                <mat-label>{{ 'EDIT_MATCH.GOAL_TIME_SECONDS' | translate }}</mat-label>
                                <input
                                    matInput
                                    type="number"
                                    min="0"
                                    [attr.aria-label]="'EDIT_MATCH.GOAL_TIME_ARIA' | translate"
                                    title="{{ 'EDIT_MATCH.GOAL_TIME_ARIA' | translate }}"
                                    [(ngModel)]="goal.elapsedSeconds"
                                />
                            </mat-form-field>
                            <button
                                mat-icon-button
                                color="warn"
                                [matTooltip]="'EDIT_MATCH.REMOVE_GOAL' | translate"
                                (click)="removeGoal(setIx, goalIx)"
                            >
                                <mat-icon>delete</mat-icon>
                            </button>
                        </div>
                    }

                    <mat-divider class="my-3"></mat-divider>

                    <!-- Add goal -->
                    <h4 class="subsection-title">{{ 'EDIT_MATCH.ADD_GOAL_TITLE' | translate }}</h4>
                    <div class="add-goal-row">
                        <mat-form-field appearance="outline" class="new-goal-time-field">
                            <mat-label>{{ 'EDIT_MATCH.NEW_GOAL_TIME_SECONDS' | translate }}</mat-label>
                            <input
                                matInput
                                type="number"
                                min="0"
                                [attr.aria-label]="'EDIT_MATCH.NEW_GOAL_TIME_ARIA' | translate"
                                title="{{ 'EDIT_MATCH.NEW_GOAL_TIME_ARIA' | translate }}"
                                [(ngModel)]="newGoals[setIx].elapsedSeconds"
                            />
                        </mat-form-field>
                    </div>
                    <div class="add-goal-players">
                        <button mat-stroked-button (click)="addGoal(setIx, set.teamA.defense.id, SetScoreGoalRole.Defense)">
                            {{ set.teamA.defense.displayName }} ({{ 'EDIT_MATCH.ROLE_DEFENSE_SHORT' | translate }})
                        </button>
                        @if (!isOneAgainstOne()) {
                            <button mat-stroked-button (click)="addGoal(setIx, set.teamA.offense.id, SetScoreGoalRole.Offense)">
                                {{ set.teamA.offense.displayName }} ({{ 'EDIT_MATCH.ROLE_OFFENSE_SHORT' | translate }})
                            </button>
                        }
                        <button mat-stroked-button (click)="addGoal(setIx, set.teamB.defense.id, SetScoreGoalRole.Defense)">
                            {{ set.teamB.defense.displayName }} ({{ 'EDIT_MATCH.ROLE_DEFENSE_SHORT' | translate }})
                        </button>
                        @if (!isOneAgainstOne()) {
                            <button mat-stroked-button (click)="addGoal(setIx, set.teamB.offense.id, SetScoreGoalRole.Offense)">
                                {{ set.teamB.offense.displayName }} ({{ 'EDIT_MATCH.ROLE_OFFENSE_SHORT' | translate }})
                            </button>
                        }
                    </div>

                    <div class="set-actions">
                        <button mat-button color="warn" (click)="removeSet(setIx)" [disabled]="match.sets.length <= 1">
                            <mat-icon>delete</mat-icon>
                            {{ 'EDIT_MATCH.REMOVE_SET' | translate }}
                        </button>
                    </div>
                </mat-expansion-panel>
            }
        </mat-accordion>
    </section>
</mat-dialog-content>

<mat-dialog-actions align="end">
    <button mat-button (click)="close()">{{ 'COMMON.CANCEL' | translate }}</button>
    <button mat-raised-button color="primary" (click)="save()">{{ 'COMMON.SAVE' | translate }}</button>
</mat-dialog-actions>
